import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Badge } from "@workspace/amarmeds-ds/components/ui/badge";
import { Button } from "@workspace/amarmeds-ds/components/ui/button";
import { Card, CardContent } from "@workspace/amarmeds-ds/components/ui/card";

import { api, ApiError, type Rider, type RunHistory } from "../api";
import { DateRange, downloadCsv, presets, type Range } from "../DateRange";
import { dhakaTime, reasonLabel, STATUS_LABEL, taka } from "../format";

const RUN_STATUS: Record<string, string> = { assigned: "Waiting for pickup", out: "Out", done: "Done" };

// Every run in a date range, newest first: who rode, from which pharmacy, when,
// and what happened to each order on it (delivered, failed, taken off).
export default function OpsRuns() {
  const [range, setRange] = useState<Range>(presets()[2]!.range);
  const [riderId, setRiderId] = useState("");
  const riders = useQuery({ queryKey: ["riders"], queryFn: () => api<{ riders: Rider[] }>("/ops/riders") });
  const runs = useQuery({
    queryKey: ["ops-runs", range, riderId],
    queryFn: () => api<{ runs: RunHistory[] }>(`/ops/runs?from=${range.from}&to=${range.to}${riderId ? `&riderId=${riderId}` : ""}`),
    refetchInterval: 60_000,
  });
  const list = runs.data?.runs ?? [];
  const totals = list.reduce((t, r) => ({ delivered: t.delivered + r.delivered, failed: t.failed + r.failed, cash: t.cash + r.cashCollectedPaisa }), { delivered: 0, failed: 0, cash: 0 });

  const csv = () =>
    downloadCsv(`savon-runs-${range.from}-to-${range.to}.csv`, [
      ["run", "status", "rider", "pharmacy", "created", "picked_up", "done", "order", "order_status", "total_taka", "cash_taka", "reason"],
      ...list.flatMap((r) =>
        r.orders.map((o) => [r.runId, r.status, r.rider.name, r.pharmacy.name, r.createdAt, r.pickedUpAt, r.doneAt, o.orderId, o.status, o.totalPaisa / 100, o.cashCollectedPaisa === null ? null : o.cashCollectedPaisa / 100, o.reason]),
      ),
    ]);

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Run history</h1>
      <DateRange value={range} onChange={setRange} />
      <div className="flex flex-wrap items-center gap-2">
        <select className="min-h-9 rounded-md border bg-background px-2 text-sm" value={riderId} onChange={(e) => setRiderId(e.target.value)} aria-label="Rider">
          <option value="">All riders</option>
          {riders.data?.riders.map((r) => (
            <option key={r.riderId} value={r.riderId}>
              {r.name}
            </option>
          ))}
        </select>
        <span className="text-sm text-muted-foreground">
          {list.length} run{list.length === 1 ? "" : "s"} · {totals.delivered} delivered · {totals.failed} failed · {taka(totals.cash)} cash
        </span>
        <Button size="sm" variant="outline" className="ml-auto" disabled={list.length === 0} onClick={csv}>
          Download CSV
        </Button>
      </div>

      {runs.isLoading && <p className="text-muted-foreground">Loading…</p>}
      {runs.error && <p className="text-destructive">{runs.error instanceof ApiError && runs.error.code === "range_too_long" ? "Pick at most 93 days." : "Could not load runs."}</p>}
      {runs.data && list.length === 0 && <p className="text-muted-foreground">No runs in these dates.</p>}

      {list.map((r) => (
        <Card key={r.runId}>
          <CardContent className="space-y-2 p-4 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold">Run {r.runId}</span>
              <Badge variant={r.status === "done" ? "secondary" : "outline"}>{RUN_STATUS[r.status] ?? r.status}</Badge>
              <span>{r.rider.name}</span>
              <span className="text-muted-foreground">from {r.pharmacy.name}</span>
              <span className="ml-auto text-muted-foreground">
                {r.delivered} delivered · {r.failed} failed · {taka(r.cashCollectedPaisa)} cash
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              Built {dhakaTime(r.createdAt)} · picked up {dhakaTime(r.pickedUpAt)} · finished {dhakaTime(r.doneAt)}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[480px] text-sm">
                <tbody>
                  {r.orders.map((o) => (
                    <tr key={`${o.orderId}-${o.status}`} className="border-t">
                      <td className="py-1 pr-2">
                        <Link href={`/ops/orders/${o.orderId}`} className="underline">
                          {o.orderId}
                        </Link>
                      </td>
                      <td className="py-1 pr-2">{STATUS_LABEL[o.status] ?? o.status}</td>
                      <td className="py-1 pr-2">{taka(o.totalPaisa)}</td>
                      <td className="py-1 pr-2">{o.cashCollectedPaisa !== null ? `cash ${taka(o.cashCollectedPaisa)}` : ""}</td>
                      <td className="py-1 text-muted-foreground">{o.deliveredAt ? dhakaTime(o.deliveredAt) : reasonLabel(o.reason)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
